import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事倫理綱領（route=/standards/ethics）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 設立1890年（明治23年）/ 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・違反件数・罰則の具体は書かない

export default function StandardsEthicsPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/ethics']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・規範', href: '/standards' },
    { label: '執事倫理綱領' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const dignityPoints = [
    {
      en: 'Dignity',
      ja: '尊厳を守る',
      text: '依頼者とその家族を、役務の対象ではなく一人の人格として遇する。年齢・地位・資産の多寡によって態度を変えないことが、尊厳尊重の出発点です。'
    },
    {
      en: 'Agency',
      ja: '自己決定を支える',
      text: '最終的な選択は常に依頼者に属します。選択肢の整理と情報の提示は行っても、決断を急がせたり、執事の好みを押し通したりしてはなりません。'
    },
    {
      en: 'Voice',
      ja: '声なき声に耳を傾ける',
      text: '言葉にされない不安や遠慮を察知し、必要なときだけ静かに確認する。押しつけがましい介入ではなく、依頼者が本来の意思を取り戻せる余白をつくります。'
    }
  ];

  const honestyRules = [
    {
      title: '引き受けの境界を偽らない',
      body: '技能・人員・時間のいずれが不足していても「できます」と安易に請け負わない。できないことはできないと伝え、代替案や外部専門家への橋渡しを誠実に提案します。'
    },
    {
      title: '報告を曲げない',
      body: '都合の悪い事実を隠したり、美辞麗句で薄めたりしない。進捗・事故・見積りの変動は、早い段階で正確に共有することが信頼の核です。'
    },
    {
      title: '記録と記憶を一致させる',
      body: '口頭の約束も書面の指示も、後から解釈が割れないよう要点を残す。記憶違いを防ぐことは、誠実さの技術的な側面でもあります。'
    }
  ];

  const conflictItems = [
    {
      label: '贈答',
      text: '個人的な贈答や過度な歓待は、判断を曇らせる芽になり得ます。受け取りの可否と報告の手順を、勤務先の方針と協会の指針に照らして判断します。'
    },
    {
      label: '便宜',
      text: '業者選定や予約枠の融通において、私的な見返りを条件にしない。依頼者の利益と無関係な「特別扱い」は、公平性を損ないます。'
    },
    {
      label: '副次的利益',
      text: '紹介料・キックバック・自己の事業への誘導など、職務を通じて得る副次的利益は原則として避け、発生し得る場合は事前に開示します。'
    },
    {
      label: '二重の忠誠',
      text: '複数の依頼者や利害関係者が絡む場面では、どちらに立つのかを曖昧にしない。利益が衝突するときは、関与の縮小や交代を含めて検討します。'
    }
  ];

  const scopeBoundaries = [
    {
      domain: '医療',
      line: '診断・投薬・治療方針への助言は行わない',
      note: '体調の観察や受診の手配はできても、医学的判断そのものは専門家の領域です。'
    },
    {
      domain: '法務',
      line: '契約解釈・紛争方針・権利義務の断定をしない',
      note: '書類の整理や専門家への取次は支援できても、法律意見の代替にはなりません。'
    },
    {
      domain: '金融',
      line: '投資・税務・資産運用の助言をしない',
      note: '家計の事務補助と、収益を約束する助言は明確に分けます。'
    }
  ];

  const gradeEthics = [
    {
      grade: 'Associate',
      ja: '初級',
      text: '守秘・報告・引き受けの基本を型として身につける段階。倫理は知識ではなく、日々の小さな判断の積み重ねとして定着させます。'
    },
    {
      grade: 'Professional',
      ja: '中級',
      text: '複数案件や外部調整のなかで、利益相反や職務境界を自ら点検できる力。現場の速度に流されず、綱領に立ち返る習慣が問われます。'
    },
    {
      grade: 'Master',
      ja: '上級',
      text: '後進と組織の規範を示す責任。自らの行動が基準になることを自覚し、是正の文化を家やチームに根づかせることが期待されます。'
    }
  ];

  const faqItems = [
    {
      q: '倫理綱領は行動規範や守秘の基準とどう違いますか？',
      a: '倫理綱領は、職務全体を貫く価値観と原則を示す上位の指針です。具体的な振る舞いの細部は行動規範へ、情報の取り扱いは守秘の基準へと接続されます。三者は対立せず、層をなして執事の実務を支えます。'
    },
    {
      q: '依頼者から医療や投資の相談を受けた場合はどうしますか？',
      a: '傾聴と状況整理までは行えても、専門判断や推奨は行いません。適切な資格を持つ専門家への取次や、依頼者自身が比較検討できるよう中立的な選択肢の提示にとどめます。'
    },
    {
      q: '綱領に反する状況を見かけたとき、まず何をすべきですか？',
      a: '安全と依頼者の尊厳を最優先に、事実を冷静に整理したうえで所定の申し出経路を用います。噂や断定を広げず、是正に必要な情報だけを適切な相手へ届けることが求められます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事倫理綱領｜全日本執事協会が定める職務の基本原則',
    description:
      '全日本執事協会が定める執事倫理綱領。綱領を置く理由、依頼者の尊厳と自己決定、誠実と正直、利益相反の回避、公平と非差別、職務範囲の境界、違反時の申し出と是正、資格三段階との接続。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/ethics'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・規範', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事倫理綱領', item: 'https://japanbutlerassociation.com/standards/ethics' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-ethics-01.jpg"
          alt="静謐な空間に立つ執事。倫理と品位を象徴する佇まい"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Code of Ethics
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事倫理綱領</span>
              <span className="block text-[#d7b779]">職務を支える基本原則</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、認定団体として、執事が職務にあたるうえでの倫理の基本原則を「執事倫理綱領」に定めています。1890年（明治23年）の設立以来受け継がれてきた奉仕の精神を、代表理事・石井裕一（三代目頭取）のもと、現代の現場でも判断の軸となる言葉へ整えました。本頁では、綱領を置く理由から、尊厳・誠実・利益相反・公平・職務境界・是正、そして資格三段階との接続までを順に示します。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 綱領を定める理由 — 宣言文＋原則インデックス（becomeのステップ帯とは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. Why a Code
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              綱領を定める<span className="text-[#a38e65]">理由</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                執事は、家庭や組織の内側に入り、日程・食卓・来客・住まいの運営までを預かります。権限が静かに大きい職業であるからこそ、「何をしてよいか」だけでなく「何をしないか」「誰の利益を最優先にするか」を言葉にして共有する必要があります。倫理綱領は、罰則表ではなく、迷いが生じたときに立ち返る羅針盤として置かれています。
              </p>
              <p>
                協会が綱領を公開するのは、依頼者に対する約束であると同時に、執事自身を守る枠組みでもあります。曖昧な善意だけに頼ると、好意が過干渉になり、便宜が利益相反に変わり、専門外の助言が信頼を損なう——そうした滑落を、原則の言語化によって未然に防ぐことが目的です。関連する具体の振る舞いについては
                <a href="/standards/conduct" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">行動規範</a>
                を、情報の取り扱いについては
                <a href="/standards/confidentiality" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">守秘の基準</a>
                を併せて参照してください。
              </p>
            </div>
            <blockquote className="mt-10 border-l-2 border-[#a38e65] bg-[#fbfaf7] px-6 sm:px-8 py-7">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.95]">
                「先回りの奉仕」は、依頼者の意思を超えて人生を設計することではありません。整えるのは環境であり、決めるのは常に依頼者です。綱領は、その境界線を美しく保つための共通言語です。
              </p>
              <footer className="mt-4 font-['Playfair_Display',serif] italic text-sm text-[#8f7a56] tracking-[0.06em]">
                — Guiding principle of the Association
              </footer>
            </blockquote>
            <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e2ddd2] border border-[#e2ddd2]">
              {[
                { n: '01', t: '尊厳と自己決定' },
                { n: '02', t: '誠実と正直' },
                { n: '03', t: '利益相反の回避' },
                { n: '04', t: '公平と非差別' },
                { n: '05', t: '職務範囲の遵守' },
                { n: '06', t: '申し出と是正' }
              ].map((item) => (
                <li key={item.n} className="bg-white px-5 py-5 flex items-baseline gap-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-lg tracking-[0.08em]">{item.n}</span>
                  <span className="jba-display font-medium text-[#1a2942] text-base">{item.t}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* S2. 尊厳と自己決定 — 縦三点＋右画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Dignity & Agency
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              依頼者の尊厳と自己決定の尊重
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start mb-10">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  倫理の第一原則は、依頼者とその家族の尊厳を侵さないことです。執事は生活の機微に触れますが、それは支配や監督の権限を意味しません。言葉遣い、視線の高さ、入室の間、提案のトーン——すべてが「相手が主役である」ことを示すためにあります。
                </p>
                <p>
                  自己決定の尊重は、沈黙を守ることと矛盾しません。必要な情報を過不足なく整え、選択肢の長短を中立に示し、決まった方針には静かに従う。執事の専門性は、決断を奪うことではなく、決断の質を高める環境をつくることに発揮されます。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-ethics-02.jpg"
                alt="依頼者の意向を丁寧に確認し合う執事たちの打ち合わせ"
                className="w-full h-64 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {dignityPoints.map((p) => (
                <div key={p.en} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. 誠実と正直 — 紺帯＋三枚パネル */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Honesty
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              誠実と正直 — できないことを引き受けない
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              誠実さは愛想のよさではありません。依頼者の期待に応える意欲と、現実の制約を正確に伝える勇気の両立です。報告を曲げないこと、できないことを引き受けないことは、短期の評価より長期の信頼を選ぶ行為です。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">INTEGRITY</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                請け負い・報告・記録の三点で誠実を測る
              </span>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {honestyRules.map((rule, idx) => (
                <div key={rule.title} className="border border-[#e2ddd2] bg-[#fbfaf7] p-7 flex flex-col">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl mb-3">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{rule.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] flex-1">{rule.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <img
                src="/images/generated/jba-std-ethics-03.jpg"
                alt="報告内容と現場の品質を突き合わせる手元。正直な記録を重んじる場面"
                className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 利益相反 — 細罫定義リスト型 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Conflicts of Interest
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              利益相反の回避 — 贈答・便宜・副次的利益
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                執事は業者・施設・外部専門家と日常的に接点を持ちます。その位置は便利であると同時に、私的利益が入り込みやすい地点でもあります。利益相反の回避とは、疑念を持たれる状況そのものを減らす姿勢です。受け取ってよいもの、断るべきもの、開示が必要なものを、感覚ではなく原則で扱います。
              </p>
              <p>
                副次的利益が完全にゼロの世界は稀です。重要なのは、発生し得る利害を隠さず、依頼者の判断材料として差し出せる透明性です。便宜供与の依頼を受けたときも、「誰の利益か」を一拍置いて問い直す習慣が、綱領の実践になります。
              </p>
            </div>
            <div className="grid lg:grid-cols-[1fr_320px] gap-10 items-start">
              <dl className="border-t-2 border-[#1a2942]">
                {conflictItems.map((c) => (
                  <div key={c.label} className="grid sm:grid-cols-[140px_1fr] gap-3 sm:gap-6 border-b border-[#e2ddd2] py-5">
                    <dt className="jba-display font-medium text-[#1a2942] text-lg flex items-start gap-2">
                      <span className="text-[#a38e65] shrink-0">▸</span>
                      {c.label}
                    </dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{c.text}</dd>
                  </div>
                ))}
              </dl>
              <img
                src="/images/generated/jba-std-ethics-04.jpg"
                alt="倫理と実務の境界を学ぶ研修の一場面"
                className="w-full h-64 lg:h-full min-h-[280px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S5. 公平と非差別 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Fairness
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              公平と非差別
            </h2>
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  公平とは、すべての人に同じ手順を機械的に当てはめることではありません。必要に応じた配慮を行いつつ、根拠のない優遇や排除をしないことです。出自、国籍、性別、年齢、信条、障害の有無、雇用上の地位などによって、礼節の質を下げたり、情報を伏せたりしてはなりません。
                </p>
                <p>
                  家庭内や組織内に複数の関係者がいる場合、執事は特定の一方だけに過度に与しないよう注意します。主人の方針が最優先であることは前提としつつ、家族・ゲスト・スタッフへの接遇に品位の落差をつくらないことが、場全体の安定につながります。
                </p>
                <p>
                  非差別の実践は、言葉の選択にも及びます。冗談や慣習に紛れた侮蔑を放置せず、必要なときは静かに軌道修正する。目立つ糾弾ではなく、場の空気を保ったまま尊厳を回復する——それが執事にふさわしい介入の形です。
                </p>
              </div>
              <div className="border border-[#e2ddd2] bg-[#fbfaf7] p-7 sm:p-8">
                <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-4">Equal regard</p>
                <ul className="space-y-4 border-t border-[#e2ddd2] pt-5">
                  {[
                    '接遇の基本手順を、相手の属性で省略しない',
                    '情報共有の範囲を、私的好悪で広げたり狭めたりしない',
                    '外部パートナーへの発注基準を、個人的関係だけで決めない',
                    'ハラスメントや排除の兆候には、早期に適切な経路で対応する'
                  ].map((line) => (
                    <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                      <span className="text-[#a38e65] shrink-0">—</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <img
                    src="/images/generated/jba-std-ethics-05.jpg"
                    alt="多様な関係者と整然と協働する実務のイメージ"
                    className="w-full h-48 object-cover border border-[#a38e65]/30"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* S6. 職務範囲の逸脱をしない */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Scope of Practice
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              職務範囲の逸脱をしない
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
              <p>
                信頼が厚いほど、「何でも相談できる人」になりやすいのが執事という立場です。しかし倫理綱領は、医療・法務・金融の助言を職務の外に置きます。善意の越境は、ときに依頼者を誤った安心へ導き、専門家の関与を遅らせます。できない領域を明確にすることは、冷淡さではなく責任です。
              </p>
              <p>
                認められるのは、観察に基づく事実の共有、予約や資料準備、適切な専門家への取次、依頼者が比較検討するための中立的な情報整理です。推奨や断定、収益や治癒を匂わせる表現は避けます。境界に迷ったときは、一人で抱えず、上位者や協会の指針に照らして確認します。
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {scopeBoundaries.map((b) => (
                <div key={b.domain} className="bg-[#1a2942] p-6 sm:p-7">
                  <span className="block font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.14em] text-sm mb-3">{b.domain}</span>
                  <h3 className="jba-display font-medium text-white text-lg mb-3 leading-[1.5]">{b.line}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">{b.note}</p>
                </div>
              ))}
            </div>
            <div className="border border-[#a38e65]/40 bg-white px-6 py-5 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">
              職務の全体像や、執事という仕事そのものの捉え方については
              <a href="/butler" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事という仕事</a>
              もあわせてご覧ください。倫理は孤立した条文ではなく、職業理解の延長線上にあります。
            </div>
          </section>

          {/* S7. 申し出と是正 — 横流れプロセス */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Remedy
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              綱領違反が起きたときの申し出と是正
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                どれほど原則を掲げても、現場では逸脱や誤解が生じ得ます。倫理綱領は、完璧な人間を前提にするのではなく、問題が起きたときに隠さず扱い、再発を防ぐ文化を前提にしています。申し出は攻撃ではなく、依頼者と職業全体を守るための手続きです。
              </p>
              <p>
                是正の要点は、事実の保全、関係者の安全、再発防止の三点にあります。憶測で人を断罪せず、必要な範囲でのみ情報を共有し、個人の反省で終わらせず手順や教育へ還元する。協会の基準群は、その循環を支えるために
                <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・規範のハブ</a>
                から横断的に参照できるよう整理されています。
              </p>
            </div>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#1a2942]">
              {[
                { step: '01', title: '安全の確保', body: '人の尊厳と身体・心理の安全を最優先に、緊急性のある事柄から手を打ちます。' },
                { step: '02', title: '事実の整理', body: '見聞きした内容を時系列で残し、推測と確認済み事項を分けて記録します。' },
                { step: '03', title: '所定の申し出', body: '勤務先の経路や協会が示す窓口など、適切な相手へ必要な情報だけを届けます。' },
                { step: '04', title: '是正と学び', body: '再発を防ぐ手順の見直しと、関係者へのフィードバックを行い、沈黙の再発を防ぎます。' }
              ].map((s) => (
                <li key={s.step} className="border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.step}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-2">{s.title}</span>
                  <span className="block font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{s.body}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* S8. 資格三段階との接続 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Credentials
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              資格三段階との接続
            </h3>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
              <p>
                倫理綱領は、資格制度と切り離された飾り文句ではありません。初級Associate・中級Professional・上級Masterの各段階で求められる判断力の深さに応じて、同じ原則が異なる解像度で問われます。専門資格で扱うワインや邸宅管理、旅程などの領域でも、利益相反や職務境界の感覚は共通して持ち込まれます。
              </p>
              <p>
                資格は到達点ではなく、綱領を現場で再現し続けるための基盤です。級が上がるほど、自分一人の正しさより、周囲が正しい判断をしやすい環境を整える責任が重なります。制度の詳細は
                <a href="/qualifications" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">資格制度</a>
                をご覧ください。
              </p>
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-stretch">
              <div className="space-y-0 border-t border-[#1a2942]">
                {gradeEthics.map((g) => (
                  <div key={g.grade} className="grid sm:grid-cols-[160px_1fr] gap-4 border-b border-[#e2ddd2] py-6">
                    <div>
                      <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.1em] mb-1">{g.grade}</span>
                      <span className="jba-display font-medium text-[#1a2942] text-lg">{g.ja}</span>
                    </div>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{g.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-4">
                <img
                  src="/images/generated/jba-std-ethics-06.jpg"
                  alt="後進に規範を示す上級執事のリーダーシップを想起させる姿"
                  className="w-full h-56 lg:flex-1 object-cover border border-[#a38e65]/30"
                />
                <a href="/qualifications" className={BTN08_ON_LIGHT}>
                  資格制度の全体像へ
                </a>
              </div>
            </div>
          </section>

          {/* S9. FAQ ＋ 関連規範導線（統合: よくある問い / 規範を横断して読む） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Questions & Related Standards
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問いと、規範を横断して読む
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-12">
              <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-6">
                規範を横断して読む
              </h3>
              <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                  倫理綱領は、行動の細部や守秘の実務と合わせて初めて機能します。基準のハブから関連頁へ進み、資格や職業理解と重ねて読み込んでください。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの現場に耐える倫理の言葉を守り続けます。
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                  <a href="/standards" className={BTN08_ON_NAVY}>
                    基準・規範のハブへ
                  </a>
                  <a href="/standards/conduct" className={BTN08_ON_NAVY}>
                    行動規範を読む
                  </a>
                  <a href="/standards/confidentiality" className={BTN08_ON_NAVY}>
                    守秘の基準を読む
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
